import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Globe, Award } from "lucide-react";

const TEAM_IMG =
  "https://customer-assets.emergentagent.com/job_compliance-platform-12/artifacts/ddjompnz_Nurse%202.jpg";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
              About QC4Health
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              We&apos;re on a mission to simplify clinical governance for every hospital.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              QC4Health, built by TMR Global, has helped 100+ hospitals since 2015
              move away from fragmented, spreadsheet-driven governance — and
              towards a single, AI-supported platform that clinicians actually
              use.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-200">
              <img
                src={TEAM_IMG}
                alt="Hospital professionals"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: "Instant Clarity. Big Heart.",
                body: "We build software that gives clinicians their time back — so they can focus on the people who matter most.",
              },
              {
                icon: Award,
                title: "Enterprise-grade, out-of-the-box.",
                body: "Every module is production-ready, connected to a shared risk engine and configured to real healthcare workflows.",
              },
              {
                icon: Globe,
                title: "Asia Pacific & beyond.",
                body: "Headquartered across Australia and Hong Kong, we support healthcare organisations wherever governance matters.",
              },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
                  data-testid={`about-value-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#22A34A]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#22A34A]" />
                  </div>
                  <div className="font-heading text-xl font-semibold text-slate-900 mb-3">
                    {v.title}
                  </div>
                  <p className="text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 md:py-24 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
            Our Story
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
            Built with hospitals, for hospitals — since 2015.
          </h2>
          <div className="mt-8 space-y-6 text-slate-600 text-lg leading-relaxed">
            <p>
              QC4Health began with a simple observation: clinical governance
              was fragmented. Risk sat in one system. Incidents in another.
              Credentials in spreadsheets. Audit was a manual scramble in the
              weeks before accreditation.
            </p>
            <p>
              We set out to unify all of it under a single platform with a
              shared risk engine — so that every audit question, every
              incident, every policy update could trigger real-time
              accountability across the organisation.
            </p>
            <p>
              A decade later, QC4Health is trusted by 100+ hospitals across the
              Asia Pacific region. Our embedded AI now drafts policies, flags
              emerging risks and prepares accreditation-ready evidence — all
              woven into the workflows clinicians already use.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
            Want to see how we can help your organisation?
          </h2>
          <Link
            to="/book-demo"
            data-testid="about-cta-book-demo-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
          >
            Book a 60-minute demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
